import {
  useTranslate, TextField, EditButton, DeleteButton, TopToolbar,
  SimpleForm, required, Toolbar,
  SaveButton, BooleanInput,
  useRecordContext,
  Button,
  Confirm,
  useSaveContext,
  useDataProvider,
  useRefresh,
  useNotify,
  useResourceContext,
  useRedirect,
} from 'react-admin';
import {
  useEffect, useMemo, useRef, useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormContext } from 'react-hook-form';
import {
  Typography, TableCell,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { ListLive } from '@react-admin/ra-realtime';
import { Datagrid, IfCanAccess } from '@react-admin/ra-rbac';
import { CertificationCategory, ExpiryTimeGranularityType } from '@x-guard/xgac-types/xgac';
import SearchFields from '../components/fields/Filters';
import { LIST_DEBOUNCE } from '../../../config';
import { TimeGranularityField } from '../../../components/fields/TimeGranularityField';
import { CreateWithSuccess } from '../../../components/baseForms/CreateWithSuccess';
import { EditWithSuccess } from '../../../components/baseForms/EditWithSuccess';
import { GrayExpiryTimeGranularityInputWithUpperLabel } from '../components/inputs/GrayExpiryTimeGranularityInputWithUpperLabel';
import { GrayTextInputWithUpperLabel } from '../components/inputs/GrayTextInputWithUpperLabel';
import { CertifiedUsersButton } from '../../../components/buttons/CertifiedUsersButton';
import { AssetsInDialogButton } from '../../../components/buttons/assetsInDialogButton';
import { CustomReminderPeriodInput } from '../components/inputs/CustomReminderPeriodInput';
import { NotifiedPeopleFormValue, NotifiedPeopleInput } from '../components/inputs/NotifiedPeopleInput';
import TextFieldNullable from '../../../components/fields/TextFieldNullable';
import { DEFAULT_REMINDER_PERIODS } from '../../../utils/certificationsAndItemsExpiry';
import { AddCategoryDialog } from '../components/AddCategoryDialog';
import { xgacDataProvider } from '../../../dataProviders/xgacDataProvider';
import { CreateFromPresetDialog, CategoryPresetKey } from '../components/CreateFromPresetDialog';

export type CertificationCategoryPresetKey =
  | 'basic_emergency_response_officer'
  | 'bhv_team_leader'
  | 'head_of_emergency_response'
  | 'basic_vca'
  | 'vol_vca'
  | 'forklift_certificate'
  | 'reach_truck_certificate'
  | 'aerial_work_platform_certificate'
  | 'nen_3140_skilled_person'
  | 'nen_3140_installation_responsible'
  | 'respiratory_protection'
  | 'working_at_height'
  | 'basic_first_aid'
  | 'pediatric_first_aid'
  | 'sports_first_aid'
  | 'first_aid_refresher'
  | 'cpr_aed';

export type CertificationCategoryPreset = {
  presetKey: CertificationCategoryPresetKey;
  validityPeriod: CertificationCategory['validityPeriod'];
};

const PRESET_I18N_PREFIX = 'resources.certification-categories.presets';

const certificationPresets: CertificationCategoryPreset[] = [
  {
    presetKey: 'basic_emergency_response_officer',
    validityPeriod: { type: ExpiryTimeGranularityType.Year, value: 1 },
  },
  {
    presetKey: 'bhv_team_leader',
    validityPeriod: { type: ExpiryTimeGranularityType.Year, value: 1 },
  },
  {
    presetKey: 'head_of_emergency_response',
    validityPeriod: { type: ExpiryTimeGranularityType.Year, value: 1 },
  },
  {
    presetKey: 'basic_vca',
    validityPeriod: { type: ExpiryTimeGranularityType.Year, value: 10 },
  },
  {
    presetKey: 'vol_vca',
    validityPeriod: { type: ExpiryTimeGranularityType.Year, value: 10 },
  },
  {
    presetKey: 'forklift_certificate',
    validityPeriod: { type: ExpiryTimeGranularityType.Year, value: 5 },
  },
  {
    presetKey: 'reach_truck_certificate',
    validityPeriod: { type: ExpiryTimeGranularityType.Year, value: 5 },
  },
  {
    presetKey: 'aerial_work_platform_certificate',
    validityPeriod: { type: ExpiryTimeGranularityType.Year, value: 5 },
  },
  {
    presetKey: 'nen_3140_skilled_person',
    validityPeriod: { type: ExpiryTimeGranularityType.Year, value: 3 },
  },
  {
    presetKey: 'nen_3140_installation_responsible',
    validityPeriod: { type: ExpiryTimeGranularityType.Year, value: 3 },
  },
  {
    presetKey: 'respiratory_protection',
    validityPeriod: { type: ExpiryTimeGranularityType.Year, value: 1 },
  },
  {
    presetKey: 'working_at_height',
    validityPeriod: { type: ExpiryTimeGranularityType.Year, value: 5 },
  },
  {
    presetKey: 'basic_first_aid',
    validityPeriod: { type: ExpiryTimeGranularityType.Year, value: 2 },
  },
  {
    presetKey: 'pediatric_first_aid',
    validityPeriod: { type: ExpiryTimeGranularityType.Year, value: 2 },
  },
  {
    presetKey: 'sports_first_aid',
    validityPeriod: { type: ExpiryTimeGranularityType.Year, value: 2 },
  },
  {
    presetKey: 'first_aid_refresher',
    validityPeriod: { type: ExpiryTimeGranularityType.Year, value: 1 },
  },
  {
    presetKey: 'cpr_aed',
    validityPeriod: { type: ExpiryTimeGranularityType.Year, value: 1 },
  },
];

const presetToCreatePayload = (
  preset: CertificationCategoryPreset,
  translate: (key: string) => string,
  notifiedPeople?: NotifiedPeopleFormValue,
): Omit<CertificationCategory, '_id' | 'customer' | 'share' | 'createdAt' | 'updatedAt'> => ({

  name: translate(`${PRESET_I18N_PREFIX}.${preset.presetKey}.name`),
  description: translate(`${PRESET_I18N_PREFIX}.${preset.presetKey}.description`),
  validityPeriod: preset.validityPeriod,
  reminderPeriods: DEFAULT_REMINDER_PERIODS,
  notifiedPeople: {
    peopleToNotify: (notifiedPeople?.peopleToNotify ?? []).map((personId) => ({
      _id: personId,
      _ref: 'Asset',
    })),
    isCertificationHolderNotified: notifiedPeople?.isCertificationHolderNotified ?? true,
    emails: notifiedPeople?.emails ?? [],
  },
  isDocumentaryProofRequired: false,

});

const CertificationCreateFromPresetsDialog = (props: { open: boolean; onClose: () => void }) => {

  const [isLoading, setIsLoading] = useState(false);
  const [existingCategoryNames, setExistingCategoryNames] = useState<string[]>([]);
  const dataProvider = useDataProvider();
  const resource = useResourceContext();
  const notify = useNotify();
  const refresh = useRefresh();
  const translate = useTranslate();
  useEffect(() => {

    if (!props.open) return;
    xgacDataProvider.getList('certification-categories', {
      pagination: { page: 1, perPage: -1 },
    }).then(({ data }) => {

      setExistingCategoryNames(data.map((row) => row.name));

    });

  }, [props.open]);

  const handleSubmit = async (selectedPresetKeys: CategoryPresetKey[], notifiedPeople?: NotifiedPeopleFormValue) => {

    setIsLoading(true);
    try {

      const selected = certificationPresets.filter((row) => selectedPresetKeys.includes(row.presetKey));

      if (selected.length === 0) return;

      await Promise.all(
        selected.map((item) => dataProvider.create(resource || 'certification-categories', { data: presetToCreatePayload(item, translate, notifiedPeople) })),
      );
      notify(
        `${translate('resources.certification-categories.text.title_single')} ${translate('general.text.saved').toLowerCase()}`,
        { type: 'success' },
      );
      refresh();
      props.onClose();

    } catch (error) {

      if (error instanceof Error) {

        notify(error.message, { type: 'error' });

      }

    } finally {

      setIsLoading(false);

    }

  };

  const existingNamesSet = useMemo(() => new Set(existingCategoryNames), [existingCategoryNames]);

  return (
    <CreateFromPresetDialog
      open={props.open}
      onClose={props.onClose}
      onSubmit={handleSubmit}
      isSubmitting={isLoading}
      presets={certificationPresets}
      existingNamesSet={existingNamesSet}
      showCertifiedUserOption
      presetPrefix={PRESET_I18N_PREFIX}
      i18n={{
        title: 'resources.certification-categories.text.add_from_presets_title',
        subtitle: 'resources.certification-categories.text.add_from_presets_subtitle',
        searchPlaceholder: 'resources.certification-categories.presets.text.search',
        noResults: 'resources.certification-categories.text.no_results',
        presetAlreadyExists: 'resources.certification-categories.text.preset_already_exists',
        peopleToNotifyTitle: 'resources.certification-categories.text.people_to_notify_title',
        peopleToNotifyLabel: 'resources.certification-categories.fields.people_to_notify',
        peopleToNotifyHelper: 'resources.certification-categories.text.people_to_notify_helper',
      }}
      renderExtraHeadCells={() => (
        <TableCell sx={{ whiteSpace: 'nowrap' }}>
          {translate('resources.certification-categories.fields.validity_period')}
        </TableCell>
      )}
      renderExtraBodyCells={(row, textColor) => (
        <TableCell sx={{ color: textColor }}>
          {'validityPeriod' in row && (
            <>
              {row.validityPeriod.value}{' '}
              {translate(
                `resources.report-triggers.text.granularity_types.${row.validityPeriod.type}`,
              ).toLowerCase()}
            </>
          )}
        </TableCell>
      )}
    />
  );

};

const CertificationDeleteButton = () => {

  const translate = useTranslate();
  const record = useRecordContext();
  return (
    <DeleteButton
      mutationMode="pessimistic"
      confirmTitle={`${translate('ra.action.delete')} ${translate('resources.certification-categories.text.title_single').toLowerCase()} "${record?.name}"`}
    />
  );

};

export const CertificationList = () => {

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createFromPresetsDialogOpen, setCreateFromPresetsDialogOpen] = useState(false);
  const redirect = useRedirect();
  const resource = useResourceContext();
  const translate = useTranslate();

  return (
    <>
      <h3>{translate('resources.certification-categories.title')}</h3>
      <ListLive
        filters={SearchFields} debounce={LIST_DEBOUNCE} title="resources.certification-categories.title" actions={
          <IfCanAccess action="create">
            <TopToolbar>
              <Button label="general.text.add" variant="contained" size="medium" startIcon={<AddIcon/>} onClick={() => setCreateDialogOpen(true)} />
            </TopToolbar>
          </IfCanAccess>
        }>
        <Datagrid rowClick={false} bulkActionButtons={false}>
          <TextField source="name" label="general.fields.name"/>
          <CertifiedUsersButton label="resources.certification-categories.text.user_count"/>
          <TimeGranularityField source="validityPeriod" label="resources.certification-categories.fields.validity_period"/>
          <AssetsInDialogButton
            label="resources.certification-categories.fields.people_to_notify"
            resource="certification-categories" source="notifiedPeople.peopleToNotify"/>
          <TextFieldNullable
            source="description"
            label="general.fields.description"
          />
          <IfCanAccess action="edit">
            <EditButton/>
          </IfCanAccess>
          <IfCanAccess action="delete">
            <CertificationDeleteButton/>
          </IfCanAccess>
        </Datagrid>
      </ListLive>
      <AddCategoryDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onAddFromPresets={() => {

          setCreateFromPresetsDialogOpen(true);
          setCreateDialogOpen(false);

        }}
        onAddManually={() => redirect('create', resource)}
        dialogTitle="resources.certification-categories.text.add_category_dialog_title"
      />
      <CertificationCreateFromPresetsDialog open={createFromPresetsDialogOpen} onClose={() => setCreateFromPresetsDialogOpen(false)}/>
    </>
  );

};

const certificationTransformer = (data: any) => {

  return {
    ...data,
    notifiedPeople: {
      peopleToNotify: (data.notifiedPeople?.peopleToNotify ?? []).map((personId: string) => ({
        _id: personId,
        _ref: 'Asset',
      })),
      isCertificationHolderNotified: data.notifiedPeople?.isCertificationHolderNotified,
      emails: data.notifiedPeople?.emails ?? [],
    },
  };

};

const CertificationFields = () => {

  return (
    <>
      <GrayTextInputWithUpperLabel
        source="name"
        validate={required()}
        label="general.fields.name"
        helperText="resources.certification-categories.text.name_helper"
        isRequired
        sx={{ width: '50%' }}
        FormHelperTextProps={{
          sx: {
            margin: 0,
            marginLeft: 0,
          },
        }}
      />
      <GrayExpiryTimeGranularityInputWithUpperLabel
        label={'resources.certification-categories.fields.validity_period'}
        source="validityPeriod"
        helperText="resources.certification-categories.text.validity_period_helper"
        isRequired
      />
      <NotifiedPeopleInput
        isRequired
        showCertifiedUserOption
        label="resources.certification-categories.fields.people_to_notify"
        helperText="resources.certification-categories.text.people_to_notify_helper"
      />
      <CustomReminderPeriodInput
        helperText="resources.certification-categories.text.reminder_periods_helper"
        isRequired
      />
      <BooleanInput
        source="isDocumentaryProofRequired"
        label="resources.certification-categories.fields.is_documentary_proof_required"
        helperText="resources.certification-categories.text.documentary_proof_helper"
      />
      <GrayTextInputWithUpperLabel
        source="description"
        label="general.fields.description"
        multiline rows={4}
        sx={{ width: '50%' }}
      />
    </>
  );

};

const CertificationEditHeading = () => {

  const record = useRecordContext();
  const translate = useTranslate();

  return (
    <h4 style={{ marginBottom: '10px' }}>{translate('general.text.edit')} {'"'}{record?.name}{'"'}</h4>
  );

};

export const CertificationCreate = () => {

  const translate = useTranslate();
  const navigate = useNavigate();
  const toastLabel = `${translate('resources.certification-categories.text.title_single')} ${translate('general.text.saved').toLowerCase()}`;

  return (
    <CreateWithSuccess
      title="resources.certification-categories.title"
      toastLabel={toastLabel}
      transform={certificationTransformer}
    >
      <SimpleForm toolbar={
        <Toolbar sx={{ justifyContent: 'space-between', maxWidth: '1000px' }}>
          <Button label='ra.action.back' onClick={() => navigate(-1)} size="medium" sx={{ marginRight: '10px' }} variant="text"/>
          <SaveButton />
        </Toolbar>
      } sx={{ maxWidth: '1000px' }}>
        <h4>{translate('resources.certification-categories.text.create_title')}</h4>
        <Typography variant="body1">{translate('resources.certification-categories.text.category_explanation')}</Typography>
        <hr/>
        <CertificationFields/>
      </SimpleForm>
    </CreateWithSuccess>
  );

};

const SaveWithDocumentaryProofConfirm = () => {

  const translate = useTranslate();
  const record = useRecordContext();
  const { handleSubmit } = useFormContext();
  const { save } = useSaveContext();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const wasInitiallyRequired = useRef<boolean | undefined>();

  useEffect(() => {

    if (!record) {

      return;

    }
    wasInitiallyRequired.current = record.isDocumentaryProofRequired;

  }, [record]);

  const runSave = (values: Partial<CertificationCategory>) => {

    save?.(values);

  };
  const trySave = handleSubmit((values) => {

    if (
      wasInitiallyRequired.current
      && values.isDocumentaryProofRequired === false
    ) {

      setConfirmOpen(true);
      return;

    }
    runSave(values);

  });
  return (
    <>
      <SaveButton label="ra.action.save" variant="contained" onClick={trySave} />
      <Confirm
        isOpen={confirmOpen}
        title={translate('resources.certification-categories.text.edit_confirmation_dialog_title')}
        content={translate('resources.certification-categories.text.edit_confirmation_dialog_content')}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => {

          setConfirmOpen(false);
          handleSubmit(runSave)();

        }}
      />
    </>
  );

};

export const CertificationEdit = () => {

  const navigate = useNavigate();
  const translate = useTranslate();
  const toastLabel = `${translate('resources.certification-categories.text.title_single')} ${translate('general.text.updated').toLowerCase()}`;

  return (
    <EditWithSuccess
      title="resources.certification-categories.title"
      actions={false}
      toastLabel={toastLabel}
      transform={certificationTransformer}
    >
      <SimpleForm toolbar={
        <Toolbar sx={{ justifyContent: 'end' }}>
          <Button label="ra.action.back" onClick={() => navigate(-1)} size="medium" sx={{ marginRight: '10px' }} variant="text"/>
          <SaveWithDocumentaryProofConfirm/>
        </Toolbar>}
      sx={{ maxWidth: '1000px' }}>
        <CertificationEditHeading/>
        <CertificationFields/>
      </SimpleForm>
    </EditWithSuccess>
  );

};
